import { t as tr } from '../i18n.js'
import { Fragment, useEffect, useState, type FormEvent } from 'react'
import { ArrowUp } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { api, ApiError, type DirectoryUser, type Portfolio, type ProjectDetail, type ProjectRole, type ProjectStatus, type TaskStatus } from '../api.js'
import { fmt } from './Account.js'
import { TasksPanel } from './Tasks.js'
import { AuditList } from './Audit.js'
import { ProjectTabs } from './ProjectTabs.js'
import { atLeast, useAuth } from '../auth.js'
import { ProjectDashboardPanel } from './ProjectDashboard.js'
import { Section, jumpToSection } from '../section.js'
import { Comments, CommentsToggle } from './Comments.js'
import { useSearchParams } from 'react-router'
import { BudgetPanel } from './Rates.js'
import { GithubPanel } from './GitHub.js'
import './portfolio.css'

const STATUSES: ProjectStatus[] = ['not_started', 'active', 'on_hold', 'done']
const PROJECT_ROLES: ProjectRole[] = ['admin', 'member', 'viewer']

export function ProjectDetailPage() {
  const { projectId = '' } = useParams()
  const { user } = useAuth()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['project', projectId], queryFn: () => api<ProjectDetail>(`/projects/${projectId}`), retry: false })
  const [params] = useSearchParams()
  const jumpTask = params.get('task')
  // A mention notification links here: ?milestone=… opens that thread, ?comment=… highlights one comment.
  const jumpMilestone = params.get('milestone')
  const jumpComment = params.get('comment') ? Number(params.get('comment')) : undefined
  const portfolios = useQuery({ queryKey: ['portfolios'], queryFn: () => api<{ portfolios: Portfolio[] }>('/portfolios'), staleTime: 60_000 })
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['project', projectId] }); void qc.invalidateQueries({ queryKey: ['projects'] }); void qc.invalidateQueries({ queryKey: ['portfolios'] }); void qc.invalidateQueries({ queryKey: ['portfolio'] }) }
  useEffect(() => { if (jumpMilestone) jumpToSection('proj-milestones') }, [jumpMilestone])

  if (q.isPending) return <p className="muted">{tr("Loading…")}</p>
  if (q.isError) return <p className="bad">{tr("Project not found, or you do not have access.")} <Link to="/projects">{tr("Back to projects")}</Link></p>
  const { project: p, permissions } = q.data
  const portfolio = p.portfolioId ? portfolios.data?.portfolios.find((x) => x.id === p.portfolioId) : undefined

  return (
    <div className="boardpage">
      <ProjectTabs projectId={p.id} active="overview" canApprove={permissions.manageMembers} canSeeRates={atLeast(user, 'manager')} />
      <div className="row between">
        <div>
          <h1>{p.color && <span className="swatch" style={{ '--pc': p.color } as React.CSSProperties} aria-hidden />} {p.name} {p.archivedAt && <span className="tag">{tr("archived")}</span>}</h1>
          <p className="muted">{p.code} · {p.status.replace('_', ' ')} · timezone of truth <strong>{p.timezoneOfTruth}</strong>{p.startsOn ? ` · ${p.startsOn} → ${p.endsOn ?? '…'}` : ''}
            {p.portfolioId && <> · {tr("in")} <Link to={`/portfolios/${p.portfolioId}`} data-tip={tr("Open the portfolio: shared board, list and Gantt across its projects")}>{portfolio?.name ?? tr("portfolio")}</Link></>}</p>
        </div>
      </div>
      <Section id="proj-overview" title="Overview" subtitle={tr("description, dashboard")} defaultOpen>
        {p.description && <p style={{ whiteSpace: 'pre-wrap' }}>{p.description}</p>}
        <ProjectDashboardPanel projectId={p.id} />
      </Section>
      <Section id="proj-budget" title="Budget" subtitle={tr("hours and money caps, alerts")}>
        <BudgetPanel projectId={p.id} canEditHours={permissions.update} canEditMoney={atLeast(user, 'admin')} />
      </Section>
      <Section id="proj-tasks" title="Tasks" subtitle={tr("list view, editor, checklists")} defaultOpen={!!jumpTask}>
        <TasksPanel d={q.data} />
      </Section>
      <Section id="proj-members" title="Members" subtitle={`${q.data.members.length} people`}>
        <MembersPanel d={q.data} onChange={invalidate} />
      </Section>
      <Section id="proj-milestones" title="Milestones" subtitle={`${q.data.milestones.length}`} defaultOpen={!!jumpMilestone}>
        <MilestonesPanel d={q.data} onChange={invalidate} openMilestone={jumpMilestone} highlight={jumpComment} />
      </Section>
      <Section id="proj-discussion" title="Discussion" subtitle={tr("comments and @mentions about the project as a whole")}>
        <Comments projectId={p.id} entity={{ type: 'project', id: p.id }} canComment={q.data.permissions.logTime} members={q.data.members} highlight={jumpComment} />
      </Section>
      {permissions.manageStatuses && <Section id="proj-statuses" title="Statuses" subtitle={tr("workflow columns and transitions")}><StatusesPanel d={q.data} onChange={invalidate} /></Section>}
      {permissions.update && <Section id="proj-github" title="GitHub" subtitle={tr("import issues as tasks, sync status")}><GithubPanel d={q.data} /></Section>}
      {permissions.update && <Section id="proj-settings" title="Settings" subtitle={tr("name, dates, timezone, archive")}><SettingsPanel d={q.data} onChange={invalidate} /></Section>}
      <Section id="proj-activity" title="Activity" subtitle={tr("audit trail for this project")}>
        <AuditList endpoint={`/projects/${p.id}/activity`} compact />
      </Section>
    </div>
  )
}

function useErr() {
  const [err, setErr] = useState<string | null>(null)
  const onError = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Request failed')
  return { err, setErr, onError }
}

function MembersPanel({ d, onChange }: { d: ProjectDetail; onChange: () => void }) {
  const { project: p, members, permissions } = d
  const { err, setErr, onError } = useErr()
  const users = useQuery({ queryKey: ['users', false], queryFn: () => api<{ users: DirectoryUser[] }>('/users'), enabled: permissions.manageMembers })
  const [pick, setPick] = useState('')
  const [role, setRole] = useState<ProjectRole>('member')
  const put = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) => api(`/projects/${p.id}/members/${userId}`, { method: 'PUT', json: { role } }),
    onSuccess: () => { setPick(''); onChange() }, onError,
  })
  const del = useMutation({ mutationFn: (userId: string) => api(`/projects/${p.id}/members/${userId}`, { method: 'DELETE' }), onSuccess: onChange, onError })
  const memberIds = new Set(members.map((m) => m.userId))
  const candidates = (users.data?.users ?? []).filter((u) => !memberIds.has(u.id))

  return (
    <div className="stack">
      
      <table className="table">
        <thead><tr><th>{tr("Name")}</th><th>{tr("Project role")}</th><th>{tr("Since")}</th>{permissions.manageMembers && <th></th>}</tr></thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.userId}>
              <td><strong>{m.displayName}</strong><br /><span className="small muted">{m.email}</span></td>
              <td>{permissions.manageMembers
                ? <select value={m.role} onChange={(e) => put.mutate({ userId: m.userId, role: e.target.value as ProjectRole })}>{PROJECT_ROLES.map((r) => <option key={r} value={r}>{r === 'admin' ? 'project administrator' : r}</option>)}</select>
                : (m.role === 'admin' ? 'project administrator' : m.role)}</td>
              <td className="small">{fmt(m.addedAt)}</td>
              {permissions.manageMembers && <td><button className="link danger" onClick={() => del.mutate(m.userId)}>Remove</button></td>}
            </tr>
          ))}
        </tbody>
      </table>
      {permissions.manageMembers && (
        <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); if (pick) put.mutate({ userId: pick, role }) }}>
          <select value={pick} onChange={(e) => setPick(e.target.value)} required style={{ flex: 1 }}>
            <option value="">{tr("Add a person…")}</option>
            {candidates.map((u) => <option key={u.id} value={u.id}>{u.displayName} ({u.email})</option>)}
          </select>
          <select value={role} onChange={(e) => setRole(e.target.value as ProjectRole)}>{PROJECT_ROLES.map((r) => <option key={r} value={r}>{r === 'admin' ? 'project administrator' : r}</option>)}</select>
          <button className="button" disabled={!pick}>{tr("Add")}</button>
        </form>
      )}
      {err && <p className="bad">{err}</p>}
    </div>
  )
}

function MilestonesPanel({ d, onChange, openMilestone, highlight }: {
  d: ProjectDetail; onChange: () => void; openMilestone?: string | null; highlight?: number | undefined
}) {
  const { project: p, milestones, permissions } = d
  const { err, setErr, onError } = useErr()
  const [name, setName] = useState('')
  const [dueOn, setDueOn] = useState('')
  // One thread open at a time; a ?milestone= deep link opens that one.
  const [thread, setThread] = useState<string | null>(openMilestone ?? null)
  const cols = permissions.manageMilestones ? 6 : 5
  const create = useMutation({ mutationFn: () => api(`/projects/${p.id}/milestones`, { method: 'POST', json: { name, dueOn: dueOn || null } }), onSuccess: () => { setName(''); setDueOn(''); onChange() }, onError })
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api(`/projects/${p.id}/milestones/${id}`, { method: 'PATCH', json: body }), onSuccess: onChange, onError })
  const del = useMutation({ mutationFn: (id: string) => api(`/projects/${p.id}/milestones/${id}`, { method: 'DELETE' }), onSuccess: onChange, onError })
  return (
    <div className="stack">
      
      {milestones.length === 0 && <p className="muted small">{tr("No milestones yet.")}</p>}
      {milestones.length > 0 && (
        <table className="table">
          <thead><tr><th></th><th>{tr("Milestone")}</th><th>{tr("Due")}</th><th>{tr("Completed")}</th><th></th>{permissions.manageMilestones && <th></th>}</tr></thead>
          <tbody>
            {milestones.map((m) => (
              <Fragment key={m.id}>
                <tr className={m.completedAt ? 'muted' : ''}>
                  <td><input type="checkbox" checked={!!m.completedAt} disabled={!permissions.manageMilestones} onChange={(e) => patch.mutate({ id: m.id, body: { completed: e.target.checked } })} /></td>
                  <td>{m.name}</td>
                  <td className="small">{m.dueOn ?? '—'}</td>
                  <td className="small">{fmt(m.completedAt)}</td>
                  <td className="mscomment"><CommentsToggle open={thread === m.id} onClick={() => setThread((x) => (x === m.id ? null : m.id))} /></td>
                  {permissions.manageMilestones && <td><button className="link danger" onClick={() => del.mutate(m.id)}>Delete</button></td>}
                </tr>
                {thread === m.id && (
                  <tr className="msthread"><td colSpan={cols}>
                    <Comments projectId={p.id} entity={{ type: 'milestone', id: m.id }} canComment={permissions.logTime} members={d.members} highlight={highlight} />
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      {permissions.manageMilestones && (
        <form className="row" onSubmit={(e) => { e.preventDefault(); setErr(null); create.mutate() }}>
          <input placeholder={tr("New milestone")} value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
          <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          <button className="button">{tr("Add")}</button>
        </form>
      )}
      {err && <p className="bad">{err}</p>}
    </div>
  )
}

function StatusesPanel({ d, onChange }: { d: ProjectDetail; onChange: () => void }) {
  const { project: p, statuses } = d
  const { err, setErr, onError } = useErr()
  const [name, setName] = useState('')
  const [category, setCategory] = useState<TaskStatus['category']>('todo')
  const create = useMutation({ mutationFn: () => api(`/projects/${p.id}/statuses`, { method: 'POST', json: { name, category } }), onSuccess: () => { setName(''); onChange() }, onError })
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api(`/projects/${p.id}/statuses/${id}`, { method: 'PATCH', json: body }), onSuccess: onChange, onError })
  const del = useMutation({ mutationFn: (id: string) => api(`/projects/${p.id}/statuses/${id}`, { method: 'DELETE' }), onSuccess: onChange, onError })
  return (
    <div className="stack">
      
      <p className="muted small">{tr("Each status maps to a global category (to do / in progress / done / archived) so reporting works across projects. Keep at least one per category. Archived work is left out of every count and hidden unless asked for.")}</p>
      <table className="table">
        <thead><tr><th>#</th><th>{tr("Status")}</th><th>{tr("Category")}</th><th>{tr("WIP limit")}</th><th>{tr("On enter")}</th><th></th></tr></thead>
        <tbody>
          {statuses.map((s, i) => (
            <tr key={s.id}>
              <td className="muted">{i + 1}</td>
              <td><InlineText value={s.name} onSave={(name) => patch.mutate({ id: s.id, body: { name } })} /></td>
              <td><select value={s.category} onChange={(e) => patch.mutate({ id: s.id, body: { category: e.target.value } })}>
                <option value="todo">{tr("to do")}</option><option value="in_progress">{tr("in progress")}</option><option value="done">{tr("done")}</option><option value="archived">{tr("archived")}</option></select></td>
              <td><WipField value={s.wipLimit} onSave={(wipLimit) => patch.mutate({ id: s.id, body: { wipLimit } })} /></td>
              <td className="small">
                <select value={s.automation.clearAssignee ? '__clear' : (s.automation.assigneeId ?? '')} onChange={(e) => patch.mutate({ id: s.id, body: { automation: { ...s.automation, assigneeId: e.target.value && e.target.value !== '__clear' ? e.target.value : null, clearAssignee: e.target.value === '__clear' } } })}>
                  <option value="">{tr("assignee: keep")}</option><option value="__clear">{tr("assignee: clear")}</option>{d.members.map((m) => <option key={m.userId} value={m.userId}>assign {m.displayName}</option>)}</select>{' '}
                <select value={s.automation.priority ?? ''} onChange={(e) => patch.mutate({ id: s.id, body: { automation: { ...s.automation, priority: e.target.value || null } } })}>
                  <option value="">{tr("priority: keep")}</option>{['urgent', 'high', 'medium', 'low'].map((x) => <option key={x} value={x}>priority: {x}</option>)}</select>{' '}
                <input type="number" min="0" placeholder={tr("due +days")} style={{ width: '5.5rem' }} defaultValue={s.automation.dueInDays ?? ''} onBlur={(e) => patch.mutate({ id: s.id, body: { automation: { ...s.automation, dueInDays: e.target.value === '' ? null : Number(e.target.value) } } })} />
              </td>
              <td>
                {i > 0 && <button className="link" onClick={() => { const prev = statuses[i - 1]!; patch.mutate({ id: s.id, body: { position: prev.position } }); patch.mutate({ id: prev.id, body: { position: s.position } }) }} data-tip={tr("Move this status up one place")} aria-label={tr("Move up")}><ArrowUp size={14} strokeWidth={2} aria-hidden /></button>}{' '}
                <button className="link danger" onClick={() => del.mutate(s.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form className="row" onSubmit={(e) => { e.preventDefault(); setErr(null); create.mutate() }}>
        <input placeholder={tr("New status")} value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} />
        <select value={category} onChange={(e) => setCategory(e.target.value as typeof category)}><option value="todo">{tr("to do")}</option><option value="in_progress">{tr("in progress")}</option><option value="done">{tr("done")}</option><option value="archived">{tr("archived")}</option></select>
        <button className="button">{tr("Add")}</button>
      </form>
      {err && <p className="bad">{err}</p>}
    </div>
  )
}

function WipField({ value, onSave }: { value: number | null; onSave: (v: number | null) => void }) {
  const [v, setV] = useState(value === null ? '' : String(value))
  return <input type="number" min="1" placeholder="—" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { const n = v === '' ? null : Number(v); if (n !== value) onSave(n) }} style={{ width: '5rem' }} />
}

function InlineText({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [v, setV] = useState(value)
  return <input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if (v.trim() && v !== value) onSave(v.trim()) }} maxLength={60} />
}

function SettingsPanel({ d, onChange }: { d: ProjectDetail; onChange: () => void }) {
  const { project: p, permissions } = d
  const { user } = useAuth()
  const { err, setErr, onError } = useErr()
  const portfolios = useQuery({ queryKey: ['portfolios'], queryFn: () => api<{ portfolios: Portfolio[] }>('/portfolios') })
  const [name, setName] = useState(p.name)
  const [description, setDescription] = useState(p.description ?? '')
  const [status, setStatus] = useState<ProjectStatus>(p.status)
  const [startsOn, setStartsOn] = useState(p.startsOn ?? '')
  const [endsOn, setEndsOn] = useState(p.endsOn ?? '')
  const [portfolioId, setPortfolioId] = useState(p.portfolioId ?? '')
  const [isPrivate, setIsPrivate] = useState(p.isPrivate)
  const [color, setColor] = useState<string | null>(p.color)
  const isOwner = !!user && user.id === p.ownerId
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: () => api(`/projects/${p.id}`, { method: 'PATCH', json: { name, description: description || null, status: status === 'archived' ? undefined : status, startsOn: startsOn || null, endsOn: endsOn || null, portfolioId: portfolioId || null, color, ...(isOwner ? { isPrivate } : {}) } }),
    onSuccess: () => { setSaved(true); onChange() }, onError,
  })
  const archive = useMutation({ mutationFn: (archived: boolean) => api(`/projects/${p.id}/archive`, { method: 'POST', json: { archived } }), onSuccess: onChange, onError })
  return (
    <div className="stack">
      
      <form className="stack" onSubmit={(e) => { e.preventDefault(); setErr(null); setSaved(false); save.mutate() }}>
        <label>{tr("Name")}<input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} /></label>
        <label>{tr("Description")}<textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        <div className="row">
          <label>{tr("Status")}<select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)} disabled={!!p.archivedAt}>{STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}{p.archivedAt && <option value="archived">{tr("archived")}</option>}</select></label>
          <label>{tr("Starts")}<input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} /></label>
          <label>{tr("Ends")}<input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} /></label>
          <label style={{ flex: 1 }}>{tr("Portfolio")}<select value={portfolioId} onChange={(e) => setPortfolioId(e.target.value)} data-tip={tr("Moving a project out of a portfolio is refused while it has cross-project links")}><option value="">—</option>{portfolios.data?.portfolios.filter((x) => !x.archivedAt || x.id === p.portfolioId).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
            {p.portfolioId && <span className="hint"><Link to={`/portfolios/${p.portfolioId}`}>{tr("Open portfolio →")}</Link></span>}</label>
        </div>
        <label>{tr("Colour")}
          <span className="colorfield">
            <input type="color" value={color ?? '#4c8bf5'} onChange={(e) => setColor(e.target.value)} aria-label={tr("Project colour")} data-tip={tr("Identifies this project's tasks on portfolio boards and Gantts")} />
            {color ? <button type="button" className="link small" onClick={() => setColor(null)}>{tr("Clear")}</button> : <span className="small muted">{tr("none — portfolio views assign a palette colour")}</span>}
          </span>
        </label>
        <label>{tr("Timezone of truth")}<input value={p.timezoneOfTruth} disabled /><span className="hint">{tr("Permanent. Create a new project to use a different zone.")}</span></label>
        {isOwner && <label className="inline" data-tip={tr("A private project appears only in your own project list — no one else can see it, whatever their role")}>
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} /> {tr("Private — only you can see this project")}
        </label>}
        {err && <p className="bad">{err}</p>}
        {saved && <p className="good">{tr("Saved.")}</p>}
        <div className="row">
          <button className="button" disabled={save.isPending}>{tr("Save")}</button>
          {permissions.archive && (p.archivedAt
            ? <button type="button" className="button" onClick={() => archive.mutate(false)}>Unarchive</button>
            : <button type="button" className="button danger" onClick={() => { if (confirm('Archive this project? It becomes read-only; nothing is deleted.')) archive.mutate(true) }}>Archive</button>)}
        </div>
      </form>
    </div>
  )
}
